/*
Remarquable detail screen — mirrors Android RemarquableDetailScreen.kt :
topbar au nom de l'arbre, galerie « Tes photos (N) », puis le même rendu
que la bottom sheet de la carte (DiscoveredContent) : titre, ligne
« Arbre remarquable » orange, bloc RemarquableInfo en OutlinedCard à
bordure dorée (« Classement : X » + résumé gras + description + année +
variété), hauteur/circonférence avec médianes, ID OpenData, bouton
« Fiche espèce » (gated espèce capturée), « Voir sur la carte ».

Data sources:
- /data/remarquables-info.json — qualification + free-text fields.
- /data/arbres-paris.geojson    — speciesIndex (`sk`) de l'arbre.
- /data/arbre-attrs.json        — hauteur / circonférence / arr.
- /data/species-info.json       — médianes de l'espèce.
*/

import { navigate, kPaths, type Route } from '../router'
import { ensureArbresFeatures, type ArbreFeature } from '../data/arbresFeatures'
import { ensureRemarquablesInfo, type RemarquableEntry } from '../data/remarquablesInfo'
import { ensureSpeciesIndex, speciesNameFor, displayName, type SpeciesName } from '../data/speciesIndex'
import { ensureSpeciesInfo, speciesInfoFor, medianComparison } from '../data/speciesInfo'
import { ensureArbreAttrs, attrsFor } from '../data/arbreAttrs'
import { arrKeyLabel } from '../data/arrKey'
import { listCaptures } from '../data/captureRepository'
import { loadPhoto } from '../data/photoStore'
import { openLightbox, type LightboxPhoto } from './components/PhotoLightbox'
import { renderSkeletonBlock, renderSkeletonLines } from './components/Skeleton'
import type { Capture } from '../data/types'

interface ViewModel
{
	feature: ArbreFeature | null
	info: RemarquableEntry | null
	species: SpeciesName | null
	captures: Capture[]  // captures de CET arbre — galerie + verrou
	speciesCaptured: boolean  // l'espèce est-elle capturée ? gate « Fiche espèce »
	loadError: string | null
}

let fActiveObjectUrls: string[] = []  // photo blob URLs du mount courant, révoquées au remount

/*
Mount the screen for the given arbreId (carried by the route). Renders a
shell synchronously, hydrates async.
*/
export function mountRemarquableDetailScreen(inRoot: HTMLElement, inRoute: Route): void
{
	revokeObjectUrls()
	if (inRoute.id !== 'remarquable')
	{
		inRoot.innerHTML = '<div class="screen"><div class="placeholder">Route invalide.</div></div>'
		return
	}
	const vArbreId = inRoute.arbreId

	// Skeleton mirrors the populated layout: gallery + identity + info card.
	const vSkeleton = `
		<section class="species-card species-card-skeleton">
			${renderSkeletonBlock({ width: '40%', height: '15px' })}
			${renderSkeletonBlock({ width: '100%', height: '96px', radius: '10px' })}
		</section>
		<section class="species-card species-card-skeleton">
			${renderSkeletonBlock({ width: '70%', height: '20px' })}
			<div class="species-skeleton-stack">${renderSkeletonLines(3, '12px')}</div>
		</section>
	`
	inRoot.innerHTML = `
		<div class="screen species">
			<header class="arboretum-header">
				<button class="nav-back" id="nav-back" aria-label="Retour">←</button>
				<h1 class="display arboretum-title" id="rmq-topbar-title">Remarquable</h1>
			</header>
			<div class="species-body" id="rmq-body">
				${vSkeleton}
			</div>
		</div>
	`
	inRoot.querySelector('#nav-back')?.addEventListener('click', () => {
		if (history.length > 1) history.back()
		else navigate(kPaths.remarquables())
	})
	void hydrate(inRoot, vArbreId)
}

function revokeObjectUrls(): void
{
	for (const vUrl of fActiveObjectUrls) URL.revokeObjectURL(vUrl)
	fActiveObjectUrls = []
}

async function hydrate(inRoot: HTMLElement, inArbreId: number): Promise<void>
{
	const vBody = inRoot.querySelector<HTMLElement>('#rmq-body')
	if (!vBody) return
	const vModel = await loadViewModel(inArbreId)

	if (vModel.loadError)
	{
		vBody.innerHTML = `<div class="placeholder species-error">⚠ ${escapeHtml(vModel.loadError)}</div>`
		return
	}
	if (!vModel.feature || !vModel.info)
	{
		vBody.innerHTML = `<div class="placeholder">Cet arbre remarquable est introuvable dans le dataset.</div>`
		return
	}

	// Verrou parité Android v1.0.x : RemarquableDetailScreen n'est
	// accessible qu'aux remarquables déjà capturés. Un deep-link vers
	// /#/remarquables/{id non capturé} retombe silencieusement sur la
	// map (Android pop la page). Cf. KDoc RemarquableDetailScreen.kt :
	// « atteinte uniquement après découverte ».
	if (vModel.captures.length === 0)
	{
		navigate(kPaths.map())
		return
	}

	// Topbar dynamique — parité Android (`arbre.nomAffichage`).
	const vTitleEl = inRoot.querySelector<HTMLElement>('#rmq-topbar-title')
	if (vTitleEl) vTitleEl.textContent = vModel.species ? displayName(vModel.species) : 'Remarquable'

	vBody.innerHTML = renderViewModel(inArbreId, vModel)
	vBody.querySelector('#species-link')?.addEventListener('click', (inEvt) => {
		inEvt.preventDefault()
		if (vModel.feature) navigate(kPaths.species(vModel.feature.speciesIndex))
	})
	// "Voir sur la carte" — deep-link to the map with the arbre's id so
	// MapScreen runs the fly-to-pulse flow. Parity Android v1.0.2.
	vBody.querySelector('#show-on-map')?.addEventListener('click', () => {
		navigate(kPaths.map(inArbreId))
	})
	void hydrateGallery(vBody, vModel.captures)
}

async function loadViewModel(inArbreId: number): Promise<ViewModel>
{
	try
	{
		const [vFeatures, vInfoMap, , vAllCaptures] = await Promise.all([
			ensureArbresFeatures(),
			ensureRemarquablesInfo(),
			ensureSpeciesIndex(),
			listCaptures(),
			ensureSpeciesInfo(),  // médianes
			ensureArbreAttrs(),   // hauteur / circonférence / arr
		])
		const vFeature = vFeatures.find((inF) => inF.id === inArbreId) ?? null  // linear scan, one-off lookup
		const vInfo = vInfoMap.get(inArbreId) ?? null
		const vSpecies = vFeature ? speciesNameFor(vFeature.speciesIndex) : null
		const vCaptures = vAllCaptures.filter((inC) => inC.arbreId === inArbreId)
		const vSpeciesCaptured = vFeature !== null && vAllCaptures.some((inC) => inC.speciesIndex === vFeature.speciesIndex)
		return { feature: vFeature, info: vInfo, species: vSpecies, captures: vCaptures, speciesCaptured: vSpeciesCaptured, loadError: null }
	}
	catch (vErr)
	{
		const vMsg = vErr instanceof Error ? vErr.message : 'Erreur inconnue'
		return { feature: null, info: null, species: null, captures: [], speciesCaptured: false, loadError: vMsg }
	}
}

function renderViewModel(inArbreId: number, inModel: ViewModel): string
{
	const vSpecies = inModel.species
	// Cascade nv → nc → latin (parity Android v1.1.0). Hide the binôme
	// subtitle when the display name IS already the binôme.
	const vDisplay = vSpecies ? displayName(vSpecies) : 'Espèce inconnue'
	const vLatinForSubtitle = vSpecies ? formatLatin(vSpecies) : ''
	const vSubtitle = vLatinForSubtitle && vLatinForSubtitle !== vDisplay ? vLatinForSubtitle : ''
	const vInfo = inModel.info as RemarquableEntry  // checked non-null in caller

	// Hauteur / circonférence + médianes — parité DiscoveredContent.
	const vAttrs = attrsFor(inArbreId)
	const vStats = inModel.feature ? speciesInfoFor(inModel.feature.speciesIndex)?.stats ?? null : null
	const vDetailRows: string[] = []
	if (vAttrs && typeof vAttrs.heightM === 'number' && vAttrs.heightM > 0)
	{
		vDetailRows.push(`<div class="species-stat-line">Hauteur : ${vAttrs.heightM} m${medianComparison(vAttrs.heightM, vStats?.medianHm ?? null)}</div>`)
	}
	if (vAttrs && typeof vAttrs.circCm === 'number' && vAttrs.circCm > 0)
	{
		vDetailRows.push(`<div class="species-stat-line">Circonférence : ${vAttrs.circCm} cm${medianComparison(vAttrs.circCm, vStats?.medianCircCm ?? null)}</div>`)
	}
	if (vAttrs?.adresse)
	{
		vDetailRows.push(`<div class="species-stat-line">Adresse : ${escapeHtml(vAttrs.adresse)}</div>`)
	}
	else if (vAttrs && vAttrs.arr.kind !== 'other')
	{
		vDetailRows.push(`<div class="species-stat-line">${escapeHtml(arrKeyLabel(vAttrs.arr))}</div>`)
	}
	vDetailRows.push(`<div class="species-stat-line species-stat-id">ID OpenData : ${inArbreId}</div>`)

	return `
		<section class="species-card">
			<h3 class="species-section-title">Tes photos (${inModel.captures.length})</h3>
			<div class="species-gallery" id="rmq-gallery"></div>
		</section>

		<section class="species-card">
			<h2 class="display species-title">${escapeHtml(vDisplay)}</h2>
			${vSubtitle ? `<div class="species-latin"><em>${escapeHtml(vSubtitle)}</em></div>` : ''}
			<div class="rmq-card-line">
				<svg class="rmq-card-line-icon" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="10.5" fill="#FB8C00"/><path fill="#F5F1E6" d="M10.99 17.92 L10.99 13.87 C9.30 13.87 7.95 13.19 7.27 11.84 C6.60 11.50 6.60 10.15 6.93 9.13 C6.60 7.79 7.61 6.44 8.96 6.44 C9.30 5.42 10.65 4.75 12 4.75 C13.35 4.75 14.70 5.42 15.04 6.44 C16.39 6.44 17.40 7.79 17.07 9.13 C17.40 10.15 17.40 11.50 16.73 11.84 C16.05 13.19 14.70 13.87 13.01 13.87 L13.01 17.92 Z"/></svg>
				<span class="rmq-card-line-label">Arbre remarquable</span>
			</div>
			${renderRemarquableBlock(vInfo)}
			${vDetailRows.join('')}
		</section>

		${inModel.speciesCaptured ? `
			<section class="species-card">
				<a class="sheet-button sheet-button-outlined rmq-species-button" id="species-link" href="${escapeAttr(kPaths.species(inModel.feature?.speciesIndex ?? 0))}">
					Fiche espèce
				</a>
			</section>
		` : ''}

		<section class="species-card">
			<button class="species-action" id="show-on-map">Voir sur la carte</button>
		</section>
	`
}

/*
Bloc RemarquableInfo — parité Android RemarquableBlock : OutlinedCard
bordure dorée 1.5px, « Classement : X » (titleSmall primary), résumé
SemiBold, description bodySmall, « Planté en X » italique, « Variété :
X ».
*/
function renderRemarquableBlock(inInfo: RemarquableEntry): string
{
	const vQualif = inInfo.qualif && inInfo.qualif.length > 0 ? inInfo.qualif : null
	const vCultivar = inInfo.cultivar && inInfo.cultivar.length > 0 ? inInfo.cultivar : null
	const vAnnee = typeof inInfo.annee === 'number' && inInfo.annee > 0 ? inInfo.annee : null
	const vResume = inInfo.resume ? dedupSentences(inInfo.resume) : null
	if (!vQualif && !vResume && !inInfo.desc && !vAnnee && !vCultivar) return ''
	const vTitle = vQualif ? `Classement : ${vQualif}` : 'Pourquoi cet arbre est remarquable'
	return `
		<div class="rmq-info-block">
			<div class="rmq-info-title">${escapeHtml(vTitle)}</div>
			${vResume ? `<div class="rmq-info-resume">${escapeHtml(vResume)}</div>` : ''}
			${inInfo.desc ? `<div class="rmq-info-desc">${escapeHtml(inInfo.desc)}</div>` : ''}
			${vAnnee !== null ? `<div class="rmq-info-meta rmq-info-meta-italic">Planté en ${vAnnee}</div>` : ''}
			${vCultivar ? `<div class="rmq-info-meta">Variété : ${escapeHtml(vCultivar)}</div>` : ''}
		</div>
	`
}

/*
Galerie « Tes photos » — thumbnails + lightbox (parité PhotoGallery
Android ; la suppression de capture reste accessible via la fiche
espèce).
*/
async function hydrateGallery(inBody: HTMLElement, inCaptures: Capture[]): Promise<void>
{
	const vGallery = inBody.querySelector<HTMLElement>('#rmq-gallery')
	if (!vGallery) return
	const vPhotos: LightboxPhoto[] = []
	for (const vCap of inCaptures)
	{
		if (!vCap.photoFilename) continue
		const vBlob = await loadPhoto(vCap.photoFilename)
		if (!vBlob) continue
		const vUrl = URL.createObjectURL(vBlob)
		fActiveObjectUrls.push(vUrl)
		const vImg = document.createElement('img')
		vImg.className = 'species-thumb'
		vImg.alt = ''
		vImg.loading = 'lazy'
		vImg.src = vUrl
		const vIndex = vPhotos.length
		vImg.addEventListener('click', () => openLightbox(vPhotos, vIndex))
		vGallery.appendChild(vImg)
		vPhotos.push({ url: vUrl })
	}
}

/*
Strip back-to-back duplicate sentences (the OpenData résumé column
sometimes glues the same sentence twice, separated by \\r\\n). Build-time
already does most of this; we keep a defensive runtime pass.
*/
function dedupSentences(inText: string): string
{
	const vSentences = inText.split(/(?<=[.!?])\s+/).map((inS) => inS.trim()).filter(Boolean)
	const vSeen = new Set<string>()  // sentences encountered, dedup key is verbatim text
	const vKept: string[] = []  // unique sentences in original order
	for (const vS of vSentences)
	{
		if (vSeen.has(vS)) continue
		vSeen.add(vS)
		vKept.push(vS)
	}
	return vKept.join(' ')
}

function formatLatin(inName: SpeciesName | null): string
{
	if (!inName) return 'Espèce inconnue'
	const vEspece = inName.espece.length > 0 ? ` ${inName.espece}` : ''
	return `${inName.genre}${vEspece}`
}

function escapeHtml(inText: string): string
{
	return inText
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;')
}

function escapeAttr(inText: string): string { return escapeHtml(inText) }
